import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  Badge,
  UncontrolledTooltip,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchExpensesApprovalLists, saveExpensesApprovalLists } from "store/actions";
import { FaCheck } from "react-icons/fa";
import Swal from "sweetalert2";
import { showSaveConfirmation, showCancelConfirmation } from "common/Confirmationmessage";

const badgeStyle = {
  minWidth: '40px',
  height: '35px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  borderRadius: '20%',
};

const ExpenseTransactionApproval = () => {
  const dispatch = useDispatch();
  const [missingEmployees, setMissingEmployees] = useState([]);

  const [authDetails, setAuthDetails] = useState(null);
  const {
    approvalLists,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    approvalLists: state.ExpensesMasterReducer?.approvalLists ?? [],
    loading: state.ExpensesMasterReducer?.loading,
    error: state.ExpensesMasterReducer?.error,
    success: state.ExpensesMasterReducer?.success,
  }));
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({
    Approve: 0,
    Reject: 0,
    Discussion: 0,
  });
  const [viewDetails, setViewDetails] = useState(null);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchExpensesApprovalLists(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (approvalLists?.length > 0) {
      const initialized = approvalLists.map((row) => ({
        ...row,
        selectedOption: null,
        remarks: "",
      }));
      setData(initialized);
    }
  }, [approvalLists]);

  useEffect(() => {
    if (success) {
      dispatch(fetchExpensesApprovalLists(authDetails.unit_id));
      setCounts({ Approve: 0, Reject: 0, Discussion: 0 });
    }
  }, [success, authDetails, dispatch]);

  useEffect(() => {
    setCounts({
      Approve: data.filter((i) => i.selectedOption === "Approve").length,
      Reject: data.filter((i) => i.selectedOption === "Reject").length,
      Discussion: data.filter((i) => i.selectedOption === "Discussion").length,
    });
  }, [data]);

  const isApproved = (row) => row.a_status === "Approved";

  const handleOptionChange = (rowId, action) => {
    const updated = data.map((row) => {
      if (row.exp_claim_id === rowId && !isApproved(row)) {
        if (row.selectedOption === action) {
          return { ...row, selectedOption: null, remarks: "" };
        }
        return { ...row, selectedOption: action };
      }
      return row;
    });
    setData(updated);
  };

  const handleBulkActionChange = (action) => {
    const allSelected = data.every((row) => isApproved(row) || row.selectedOption === action);

    const updated = data.map((row) => {
      if (isApproved(row)) return row;
      return {
        ...row,
        selectedOption: allSelected ? null : action,
        remarks: allSelected ? "" : row.remarks,
      };
    });
    setData(updated);
  };

  const handleRemarksChange = (rowId, value) => {
    const updated = data.map((row) =>
      row.exp_claim_id === rowId
        ? { ...row, remarks: value }
        : row
    );
    setData(updated);
  };

  const formik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({}),
    onSubmit: () => {
      const statusMap = {
        Approve: 1,
        Reject: 2,
        Discussion: 3,
      };

      const selectedRows = data.filter(item => item.selectedOption);

      if (selectedRows.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Selection",
          text: "Please select at least one approval action.",
        });
        return;
      }
      const rowsRequiringRemarks = selectedRows.filter(item => item.selectedOption === "Reject" || item.selectedOption === "Discussion");
      const missingRemarksRows = rowsRequiringRemarks.filter(item => !item.remarks || item.remarks.trim() === "");

      if (missingRemarksRows.length > 0) {
        const uniqueNames = [...new Set(missingRemarksRows.map(i => i.emp_name))];

        Swal.fire({
          icon: "warning",
          title: "Please provide remarks for Reject / Discussion Claims",
          html: uniqueNames,
          text: "Please select at least one before proceeding.",
        });
        return;
      }

      Swal.fire({
        title: "Save Approval?",
        html: `Approve: <strong>${counts.Approve}</strong> &nbsp; Reject: <strong>${counts.Reject}</strong> &nbsp; Discussion: <strong>${counts.Discussion}</strong>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Save",
        cancelButtonText: "No",
        reverseButtons: false,
      }).then((result) => {
        if (result.isConfirmed) {
          const expeArray = selectedRows.map((item) => ({
            exp_claim_id: item.exp_claim_id,
            exp_approval_status: statusMap[item.selectedOption],
            exp_approval_remarks: item.remarks.trim(),
          }));

          const formData = new FormData();
          formData.append("lst_exp_approval", JSON.stringify({ expe: expeArray }));
          formData.append("create_by", authDetails?.user_id);

          dispatch(saveExpensesApprovalLists(formData));
          showSaveConfirmation("Approval saved successfully!");
        }
      });
    },
  });

  const RemarksCell = ({ row, handleRemarksChange }) => {
    const [value, setValue] = useState(row.original.remarks || "");

    useEffect(() => {
      setValue(row.original.remarks || "");
    }, [row.original.remarks]);

    const handleBlur = () => {
      if (row.original.selectedOption) {
        handleRemarksChange(row.original.exp_claim_id, value);
      }
    };

    return (
      <Input
        type="text"
        bsSize="sm"
        placeholder="Enter remarks"
        value={value}
        disabled={!row.original.selectedOption}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
      />
    );
  };

  const handleCancel = () => {
    showCancelConfirmation(() => {
      const reset = data.map((item) => ({
        ...item,
        selectedOption: null,
        remarks: "",
      }));
      setData(reset);
    });
  };

  const handleFileOpen = (filePath) => {
    if (filePath) {
      const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || "";
      window.open(`${baseUrl}${filePath}`, "_blank");
    }
  };

  const handleViewDetails = (row) => {
    setViewDetails(row);
  };

  const columns = [
    { Header: "S.No", Cell: ({ row }) => data.length - row.index, className: "serial-no text-center" },
    { Header: "Employee Name", accessor: "emp_name" },
    { Header: "Date", accessor: "exp_date" },
    { Header: "Head", accessor: "expense_head_name" },
    { Header: "Sub Head", accessor: "expense_subheads_name" },
    { Header: "Amount", accessor: "exp_amount" },
    {
      Header: "Status",
      Cell: ({ row }) => {
        const status = row.original.a_status;
        return (
          <span className={`badge ${status === "Approved" ? "bg-success" : status === "Rejected" ? "bg-danger" : "bg-warning"}`}>
            {status || "Pending"}
          </span>
        );
      },
    },
    {
      id: "approve",
      Header: () => (
        <div className="text-center">
          Approve <br />
          <Input type="checkbox" onChange={() => handleBulkActionChange("Approve")} />
        </div>
      ),
      Cell: ({ row }) => {
        const locked = isApproved(row.original);
        return (
          <div
            className="d-flex justify-content-center align-items-center"
            onClick={() => !locked && handleOptionChange(row.original.exp_claim_id, "Approve")}
            style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
            title={locked ? "Already Approved" : ""}
          >
            {row.original.selectedOption === "Approve" ? (
              <FaCheck color="green" size={18} />
            ) : (
              <Input type="checkbox" checked={row.original.selectedOption === "Approve"} disabled={locked} readOnly />
            )}
          </div>
        );
      },
    },
    {
      id: "reject",
      Header: () => (
        <div className="text-center">
          Reject <br />
          <Input type="checkbox" onChange={() => handleBulkActionChange("Reject")} />
        </div>
      ),
      Cell: ({ row }) => {
        const locked = isApproved(row.original);
        return (
          <div
            className="d-flex justify-content-center align-items-center"
            onClick={() => !locked && handleOptionChange(row.original.exp_claim_id, "Reject")}
            style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
            title={locked ? "Already Approved" : ""}
          >
            {row.original.selectedOption === "Reject" ? (
              <FaCheck color="red" size={18} />
            ) : (
              <Input type="checkbox" checked={row.original.selectedOption === "Reject"} disabled={locked} readOnly />
            )}
          </div>
        );
      },
    },
    {
      id: "discussion",
      Header: () => (
        <div className="text-center">
          Discussion <br />
          <Input type="checkbox" onChange={() => handleBulkActionChange("Discussion")} />
        </div>
      ),
      Cell: ({ row }) => {
        const locked = isApproved(row.original);
        return (
          <div
            className="d-flex justify-content-center align-items-center"
            onClick={() => !locked && handleOptionChange(row.original.exp_claim_id, "Discussion")}
            style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
            title={locked ? "Already Approved" : ""}
          >
            {row.original.selectedOption === "Discussion" ? (
              <FaCheck color="orange" size={18} />
            ) : (
              <Input type="checkbox" checked={row.original.selectedOption === "Discussion"} disabled={locked} readOnly />
            )}
          </div>
        );
      },
    },
    {
      Header: "Remarks",
      Cell: ({ row }) => (
        <RemarksCell row={row} handleRemarksChange={handleRemarksChange} />
      ),
    },
    {
      Header: "Action",
      Cell: (cellProps) => {
        const fileUrl = cellProps.row.original.file_path;
        const filePath = `${process.env.REACT_APP_IMAGE_BASE_URL}${fileUrl}`;
        
        return (
          <div className="d-flex gap-3">
            <a href={filePath} target="_blank" rel="noopener noreferrer">
              <i
                className={`mdi ${filePath ? "mdi-file-check" : "mdi-file-outline"} text-info font-size-18 cursor-pointer`}
                title={filePath ? "File Attached" : "Attach File"}
              />
            </a>
          </div>
        );
      },
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Expenses" breadcrumbItem="Approval" />
        <Card>
          <CardBody>
            <Row className="gy-3 text-center align-items-center">
              <Col xl={3} md={4}>
                <strong style={{ fontSize: '16px' }}>Approve: </strong>
                <Badge color="success" style={badgeStyle}>{counts.Approve}</Badge>
              </Col>
              <Col xl={3} md={4}>
                <strong style={{ fontSize: '16px' }}>Reject: </strong>
                <Badge color="danger" style={badgeStyle}>{counts.Reject}</Badge>
              </Col>
              <Col xl={3} md={4}>
                <strong style={{ fontSize: '16px' }}>Discussion: </strong>
                <Badge color="warning" style={badgeStyle}>{counts.Discussion}</Badge>
              </Col>
              <Col xl={3}>
                <Form onSubmit={formik.handleSubmit}>
                  <div className="d-flex justify-content-end gap-2 flex-wrap w-100">
                    <Button type="submit" className="btn button-save"><i className="fas fa-save me-1" /> Save</Button>
                    <Button type="button" className="btn button-cancel" onClick={handleCancel}><i className="fas fa-sync me-1" /> Cancel</Button>
                  </div>
                </Form>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={data}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>        
      </Container>
    </div>
  );
};

export default ExpenseTransactionApproval;
